import React, { useRef, useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { REPAIR_LABELS, clampRepair, formatRange } from "@/lib/pricing";

const OPEN_MAX_FACTOR = 4;

// Quick-pick chips in round steps: $10 apart for narrow ranges, $20 for wider ones (max 8 chips)
const chipsFor = (range) => {
  const top = range.max ?? range.min * OPEN_MAX_FACTOR;
  const step = top - range.min <= 70 ? 10 : 20;
  const chips = [];
  for (let v = Math.ceil(range.min / 10) * 10; v <= top && chips.length < 8; v += step) chips.push(v);
  if (range.max && chips[chips.length - 1] !== range.max) chips.push(range.max);
  return chips;
};

// Hover / click popover to fine-tune the repair price within the tier's range
export default function RepairPricePicker({ panelKey, tier, range, value, sym, onChange, active, onSelect, className, labelOverride, testidPrefix = "repair" }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const sticky = useRef(false);
  const label = labelOverride || REPAIR_LABELS[tier];
  const sliderMax = range.max ?? Math.max(range.min * OPEN_MAX_FACTOR, value);

  const openNow = () => { clearTimeout(timer.current); setOpen(true); };
  const hoverOpen = () => { if (!open) { sticky.current = false; openNow(); } };
  const closeSoon = () => {
    if (sticky.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 180);
  };
  const handleOpenChange = (o) => { setOpen(o); if (!o) sticky.current = false; };

  const handleClick = (e) => {
    e.preventDefault();
    if (!active) onSelect();
    sticky.current = true;
    openNow();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button data-on={active} onClick={handleClick} onMouseEnter={active ? hoverOpen : undefined} onMouseLeave={closeSoon}
          data-testid={`${testidPrefix}-tier-${panelKey}-${tier}`} className={className}>
          {label.short}
          <span className="font-mono-num opacity-80"> · {active ? `${sym}${value}` : formatRange(sym, range)}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} onMouseEnter={openNow} onMouseLeave={closeSoon}
        className="w-64 bg-[#111827] border-slate-700 text-slate-100 p-3 space-y-3" data-testid={`${testidPrefix}-price-popover-${panelKey}`}>
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-300">{label.en} <span className="font-zh text-amber-300/70 normal-case">{label.zh}</span></div>
            <div className="text-[11px] text-slate-500 font-mono-num">Range {formatRange(sym, range)}</div>
          </div>
          <div className="font-mono-num font-black text-amber-400 text-lg" data-testid={`${testidPrefix}-price-value-${panelKey}`}>{sym}{value}</div>
        </div>
        <Slider min={range.min} max={sliderMax} step={10} value={[value]} onValueChange={([v]) => onChange(clampRepair(range, v))}
          data-testid={`${testidPrefix}-price-slider-${panelKey}`} className="[&_[role=slider]]:bg-amber-400 [&_[role=slider]]:border-amber-400 [&_.bg-primary]:bg-amber-400" />
        <div className="flex flex-wrap gap-1.5">
          {chipsFor(range).map((v) => (
            <button key={v} data-on={value === v} onClick={() => onChange(clampRepair(range, v))} data-testid={`${testidPrefix}-chip-${panelKey}-${v}`}
              className="px-2 py-0.5 rounded-md text-[11px] font-mono-num font-semibold border border-slate-700 text-slate-300 hover:border-amber-400/60 data-[on=true]:bg-amber-400 data-[on=true]:text-slate-950 data-[on=true]:border-amber-400 transition-colors">
              {sym}{v}
            </button>
          ))}
          {range.max === null && <span className="text-[10px] text-slate-500 self-center">onwards · 起</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Custom</span>
          <div className="relative flex-1">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs">{sym}</span>
            <Input type="number" min={range.min} max={range.max ?? undefined} step="1" value={value}
              onChange={(e) => onChange(Number(e.target.value))} onBlur={(e) => onChange(clampRepair(range, e.target.value))}
              data-testid={`${testidPrefix}-price-input-${panelKey}`} className="bg-slate-950/60 border-slate-700 h-8 pl-6 text-sm font-mono-num" />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
